import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../../api/client';

interface TicketTier {
  id: string;
  name: string;
  price: string | number;
  description: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EditingState {
  id: string;
  name: string;
  price: string;
  description: string;
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '1.5rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.6rem 0.75rem',
    borderBottom: '2px solid #e0e0e0',
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#555',
    textTransform: 'uppercase' as const,
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.9rem',
    verticalAlign: 'middle' as const,
  },
  btn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  btnGold: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#c9a84c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  btnDanger: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  btnCancel: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#757575',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
    marginRight: '0.5rem',
  },
  input: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginRight: '0.5rem',
  },
  inputSmall: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  badge: {
    display: 'inline-block',
    padding: '0.2rem 0.5rem',
    borderRadius: '3px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  badgeActive: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  badgeInactive: {
    backgroundColor: '#fafafa',
    color: '#999',
  },
  formRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
    marginTop: '1rem',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  formLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#555',
    marginBottom: '0.25rem',
  },
};

export function TierManagement() {
  const [tiers, setTiers] = useState<TicketTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<EditingState | null>(null);

  // New tier form state
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  async function fetchTiers() {
    try {
      const data = await apiClient<TicketTier[]>('/legacy-ball/tiers/all');
      setTiers(data);
      setError('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load tiers.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTiers();
  }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !newPrice) return;
    setCreating(true);
    setError('');
    try {
      await apiClient('/legacy-ball/tiers', {
        method: 'POST',
        body: {
          name: newName,
          price: parseFloat(newPrice),
          ...(newDescription ? { description: newDescription } : {}),
        },
      });
      setNewName('');
      setNewPrice('');
      setNewDescription('');
      fetchTiers();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; errors?: string[] };
      const msg = apiErr.errors
        ? apiErr.errors.join(', ')
        : apiErr.message || 'Failed to create tier.';
      setError(msg);
    } finally {
      setCreating(false);
    }
  }

  function startEditing(tier: TicketTier) {
    setEditing({
      id: tier.id,
      name: tier.name,
      price: String(tier.price),
      description: tier.description,
    });
    setError('');
  }

  function cancelEditing() {
    setEditing(null);
  }

  async function handleUpdate(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setError('');
    try {
      const body: Record<string, unknown> = {};
      const original = tiers.find((t) => t.id === editing.id);
      if (original && editing.name !== original.name) body.name = editing.name;
      if (original && editing.price !== String(original.price))
        body.price = parseFloat(editing.price);
      if (original && editing.description !== original.description)
        body.description = editing.description;

      if (Object.keys(body).length === 0) {
        setEditing(null);
        return;
      }

      await apiClient(`/legacy-ball/tiers/${editing.id}`, {
        method: 'PATCH',
        body,
      });
      setEditing(null);
      fetchTiers();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; errors?: string[] };
      const msg = apiErr.errors
        ? apiErr.errors.join(', ')
        : apiErr.message || 'Failed to update tier.';
      setError(msg);
    }
  }

  async function handleDeactivate(id: string) {
    setError('');
    try {
      await apiClient(`/legacy-ball/tiers/${id}/deactivate`, {
        method: 'PATCH',
      });
      fetchTiers();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to deactivate tier.');
    }
  }

  function formatPrice(price: string | number): string {
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return `$${num.toFixed(2)}`;
  }

  if (loading) {
    return (
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Tier Management</h2>
        <p style={{ color: '#666' }}>Loading tiers...</p>
      </div>
    );
  }

  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>Tier Management</h2>
      {error && <div style={styles.error}>{error}</div>}

      {/* Tiers Table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Price</th>
            <th style={styles.th}>Description</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tiers.length === 0 && (
            <tr>
              <td style={styles.td} colSpan={5}>
                <span style={{ color: '#666' }}>No tiers created yet.</span>
              </td>
            </tr>
          )}
          {tiers.map((tier) =>
            editing && editing.id === tier.id ? (
              <tr key={tier.id}>
                <td style={styles.td}>
                  <input
                    style={styles.inputSmall}
                    value={editing.name}
                    onChange={(e) =>
                      setEditing({ ...editing, name: e.target.value })
                    }
                  />
                </td>
                <td style={styles.td}>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    style={styles.inputSmall}
                    value={editing.price}
                    onChange={(e) =>
                      setEditing({ ...editing, price: e.target.value })
                    }
                  />
                </td>
                <td style={styles.td}>
                  <input
                    style={styles.inputSmall}
                    value={editing.description}
                    onChange={(e) =>
                      setEditing({ ...editing, description: e.target.value })
                    }
                  />
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, ...styles.badgeActive }}>
                    Active
                  </span>
                </td>
                <td style={styles.td}>
                  <form
                    onSubmit={handleUpdate}
                    style={{ display: 'inline' }}
                  >
                    <button type="submit" style={styles.btn}>
                      Save
                    </button>
                    <button
                      type="button"
                      style={styles.btnCancel}
                      onClick={cancelEditing}
                    >
                      Cancel
                    </button>
                  </form>
                </td>
              </tr>
            ) : (
              <tr key={tier.id}>
                <td style={styles.td}>
                  <strong>{tier.name}</strong>
                </td>
                <td style={styles.td}>{formatPrice(tier.price)}</td>
                <td style={styles.td}>
                  <span style={{ color: tier.description ? '#333' : '#999' }}>
                    {tier.description || '—'}
                  </span>
                </td>
                <td style={styles.td}>
                  <span
                    style={{
                      ...styles.badge,
                      ...(tier.active
                        ? styles.badgeActive
                        : styles.badgeInactive),
                    }}
                  >
                    {tier.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  {tier.active && (
                    <>
                      <button
                        style={styles.btnGold}
                        onClick={() => startEditing(tier)}
                      >
                        Edit
                      </button>
                      <button
                        style={styles.btnDanger}
                        onClick={() => handleDeactivate(tier.id)}
                      >
                        Deactivate
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>

      {/* Create New Tier Form */}
      <form onSubmit={handleCreate} style={styles.formRow}>
        <div style={styles.formField}>
          <label style={styles.formLabel}>Name</label>
          <input
            style={styles.input}
            placeholder="Tier name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </div>
        <div style={styles.formField}>
          <label style={styles.formLabel}>Price</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            style={styles.input}
            placeholder="0.00"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
          />
        </div>
        <div style={styles.formField}>
          <label style={styles.formLabel}>Description (optional)</label>
          <input
            style={styles.input}
            placeholder="Perks or details"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
        </div>
        <button type="submit" style={styles.btn} disabled={creating}>
          {creating ? 'Creating...' : 'Add Tier'}
        </button>
      </form>
    </div>
  );
}
